import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useSupabase } from '../../../../../providers/SupabaseProvider';
import { X, FileText, Download, Loader2, Info, AlertCircle, Check } from 'lucide-react';
import { REQUISITION_FORM_FIELDS } from '../../../../../utils/requisitionFormFieldNames';
import {
  DEFAULT_REQUISITION_CHECKBOX_MAPPING,
  resolveCategorySemantics,
  resolveSampleKind,
} from '../../../../../utils/requisitionCheckboxMapping';

interface RequisitionFormModalProps {
  show: boolean;
  onClose: () => void;
  sample: any;
  customerId: string;
}

interface FormTemplate {
  id: string;
  name: string;
  file_path: string;
  file_url: string;
  file_type: string;
  created_at: string;
  field_mappings?: Record<string, string | string[]>;
  checkbox_activation_config?: Record<string, boolean>;
  direct_field_activation_config?: Record<string, boolean>;
}

interface Customer {
  id: string;
  first_name: string;
  last_name: string;
  gender: string;
  birth_date: string;
  customer_number: string | null;
  id_number: string | null;
}

const RequisitionFormModal: React.FC<RequisitionFormModalProps> = ({
  show,
  onClose,
  sample,
  customerId
}) => {
  const supabase = useSupabase();
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [templates, setTemplates] = useState<FormTemplate[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [substances, setSubstances] = useState<any[]>([]);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    if (show) {
      loadData();
    }
  }, [show, customerId]);

  useEffect(() => {
    if (!show) {
      setSuccess(null);
      setError(null);
    }
  }, [show]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not found');

      const { data: orgUser, error: orgUserError } = await supabase
        .from('organization_users')
        .select('organization_id')
        .eq('user_id', user.id)
        .single();

      if (orgUserError) throw orgUserError;

      const { data: templatesData, error: templatesError } = await supabase
        .from('form_templates')
        .select('*')
        .eq('organization_id', orgUser.organization_id)
        .order('created_at', { ascending: false });

      if (templatesError) throw templatesError;
      setTemplates(templatesData || []);

      if (templatesData && templatesData.length > 0) {
        setSelectedTemplateId(templatesData[0].id);
      }

      const { data: customerData, error: customerError } = await supabase
        .from('customers')
        .select('id, first_name, last_name, gender, birth_date, customer_number, id_number')
        .eq('id', customerId)
        .single();

      if (customerError) throw customerError;
      setCustomer(customerData);

      if (sample && sample.id) {
        const { data: substancesData, error: substancesError } = await supabase
          .from('sample_substance_details')
          .select('*')
          .eq('sample_id', sample.id);

        if (substancesError) throw substancesError;
        setSubstances(substancesData || []);
      }
    } catch (err: any) {
      console.error('Error loading data:', err);
      setError('Fehler beim Laden der Daten: ' + (err.message || 'Unbekannter Fehler'));
    } finally {
      setLoading(false);
    }
  };

  const generateRequisitionForm = async () => {
    if (!selectedTemplateId || !customer) {
      setError('Bitte wählen Sie eine Vorlage aus');
      return;
    }

    setGenerating(true);
    setError(null);
    setSuccess(null);

    try {
      const template = templates.find(t => t.id === selectedTemplateId);
      if (!template) throw new Error('Vorlage nicht gefunden');
      const fieldMappings = template.field_mappings || {};
      const checkboxActivationConfig = template.checkbox_activation_config || {};
      const directFieldActivationConfig = template.direct_field_activation_config || {};

      const { data: pdfFile, error: downloadError } = await supabase.storage
        .from('form_templates')
        .download(template.file_path);

      if (downloadError) throw downloadError;

      const formData = new FormData();
      formData.append('template', pdfFile, 'template.pdf');
      const resolveTemplateFieldNames = (standardFieldName: string): string[] => {
        const mappingValue = fieldMappings[standardFieldName];
        if (Array.isArray(mappingValue)) {
          const names = mappingValue.map((name) => name?.trim()).filter(Boolean) as string[];
          return names.length > 0 ? names : [standardFieldName];
        }
        if (typeof mappingValue === 'string' && mappingValue.trim().length > 0) {
          return [mappingValue.trim()];
        }
        return [standardFieldName];
      };
      const appendMappedField = (standardFieldName: string, value: string) =>
        resolveTemplateFieldNames(standardFieldName).forEach((resolvedFieldName) => formData.append(resolvedFieldName, value));
      const setMappedField = (standardFieldName: string, value: string) =>
        resolveTemplateFieldNames(standardFieldName).forEach((resolvedFieldName) => formData.set(resolvedFieldName, value));
      
      appendMappedField(REQUISITION_FORM_FIELDS.CUSTOMER_NAME, `${customer.first_name} ${customer.last_name}`);
      // Kundennummer nur befüllen, wenn im Kundendatensatz vorhanden.
      if (customer.customer_number) {
        appendMappedField(REQUISITION_FORM_FIELDS.CUSTOMER_NUMBER, customer.customer_number);
      }
      // Format birth date as DD.MM.YYYY
      const birthDate = new Date(customer.birth_date);
      const formattedBirthDate = `${String(birthDate.getDate()).padStart(2, '0')}.${String(birthDate.getMonth() + 1).padStart(2, '0')}.${birthDate.getFullYear()}`;
      appendMappedField(REQUISITION_FORM_FIELDS.CUSTOMER_BIRTH_DATE, formattedBirthDate); 
      appendMappedField(REQUISITION_FORM_FIELDS.CUSTOMER_GENDER, 
        customer.gender === 'male' ? 'männlich' : 
        customer.gender === 'female' ? 'weiblich' : 'divers'
      );
      appendMappedField(REQUISITION_FORM_FIELDS.CUSTOMER_GENDER_MALE, customer.gender === 'male' ? 'Yes' : 'Off');
      appendMappedField(REQUISITION_FORM_FIELDS.CUSTOMER_GENDER_FEMALE, customer.gender === 'female' ? 'Yes' : 'Off');
      appendMappedField(REQUISITION_FORM_FIELDS.CUSTOMER_GENDER_DIVERSE, customer.gender === 'diverse' ? 'Yes' : 'Off');
      
      if (customer.id_number) {
        appendMappedField(REQUISITION_FORM_FIELDS.CUSTOMER_ID_NUMBER, customer.id_number);
      }
      
      if (sample.collection_date) {
        // Format collection date as DD.MM.YYYY
        const collectionDate = new Date(sample.collection_date);
        const formattedCollectionDate = `${String(collectionDate.getDate()).padStart(2, '0')}.${String(collectionDate.getMonth() + 1).padStart(2, '0')}.${collectionDate.getFullYear()}`;
        appendMappedField(REQUISITION_FORM_FIELDS.COLLECTION_DATE, formattedCollectionDate);
        
        // Ensure collection_time is properly handled
        if (sample.collection_time && typeof sample.collection_time === 'string') {
          const timeValue = sample.collection_time.substring(0, 5);
          console.log('Setting collection time:', timeValue);
          appendMappedField(REQUISITION_FORM_FIELDS.COLLECTION_TIME, timeValue);
        }
      } else if (sample.scheduled_date) {
        // Format scheduled date as DD.MM.YYYY
        const scheduledDate = new Date(sample.scheduled_date);
        const formattedScheduledDate = `${String(scheduledDate.getDate()).padStart(2, '0')}.${String(scheduledDate.getMonth() + 1).padStart(2, '0')}.${scheduledDate.getFullYear()}`;
        appendMappedField(REQUISITION_FORM_FIELDS.COLLECTION_DATE, formattedScheduledDate);
        
        // Ensure scheduled_time is properly handled
        if (sample.scheduled_time && typeof sample.scheduled_time === 'string') {
          const timeValue = sample.scheduled_time.substring(0, 5);
          console.log('Setting scheduled time:', timeValue);
          appendMappedField(REQUISITION_FORM_FIELDS.COLLECTION_TIME, timeValue);
        }
      }
      
      // Always set all checkboxes to ensure they're properly initialized
      // Default all checkboxes to 'Off'
      // Initialize all checkboxes to Off
      const checkboxFields = [
        REQUISITION_FORM_FIELDS.URINE_ALCOHOL, 
        REQUISITION_FORM_FIELDS.URINE_DRUGS, 
        REQUISITION_FORM_FIELDS.URINE_MEDICATION, 
        REQUISITION_FORM_FIELDS.URINE_OPIATES,
        REQUISITION_FORM_FIELDS.HAIR_ALCOHOL, 
        REQUISITION_FORM_FIELDS.HAIR_DRUGS, 
        REQUISITION_FORM_FIELDS.HAIR_MEDICATION, 
        REQUISITION_FORM_FIELDS.HAIR_OPIATES, 
        REQUISITION_FORM_FIELDS.BLOOD_ALCOHOL, 
        REQUISITION_FORM_FIELDS.CUSTOMER_GENDER_MALE, 
        REQUISITION_FORM_FIELDS.CUSTOMER_GENDER_FEMALE, 
        REQUISITION_FORM_FIELDS.CUSTOMER_GENDER_DIVERSE,
        REQUISITION_FORM_FIELDS.EXTRACTION_SITE_HEAD, 
        REQUISITION_FORM_FIELDS.EXTRACTION_SITE_OTHER,
        REQUISITION_FORM_FIELDS.COSMETIC_TREATMENT_NONE, 
        REQUISITION_FORM_FIELDS.COSMETIC_TREATMENT_YES
      ];
      
      checkboxFields.forEach(field => {
        setMappedField(field, 'Off');
      });
      
      // Determine which checkboxes should be checked based on sample type and substances
      const sampleKind = resolveSampleKind(sample.sample_type_name, DEFAULT_REQUISITION_CHECKBOX_MAPPING);
      const categoryValues = substances.flatMap((substance) => {
        const values: string[] = [];
        if (typeof substance.category_name === 'string') values.push(substance.category_name);
        if (typeof substance.substance_name === 'string') values.push(substance.substance_name);
        return values;
      });
      const categorySemantics = resolveCategorySemantics(categoryValues, DEFAULT_REQUISITION_CHECKBOX_MAPPING);
      
      // Explicitly set gender checkboxes again to ensure they're properly set
      setMappedField(REQUISITION_FORM_FIELDS.CUSTOMER_GENDER_MALE, customer.gender === 'male' ? 'Yes' : 'Off');
      setMappedField(REQUISITION_FORM_FIELDS.CUSTOMER_GENDER_FEMALE, customer.gender === 'female' ? 'Yes' : 'Off');
      setMappedField(REQUISITION_FORM_FIELDS.CUSTOMER_GENDER_DIVERSE, customer.gender === 'diverse' ? 'Yes' : 'Off'); 
      
      if (sampleKind === 'urine') {
        // Explicitly set checkbox values for Urin
        setMappedField(REQUISITION_FORM_FIELDS.URINE_ALCOHOL, categorySemantics.alcohol ? 'Yes' : 'Off');
        setMappedField(REQUISITION_FORM_FIELDS.URINE_DRUGS, categorySemantics.drugs ? 'Yes' : 'Off');
        setMappedField(REQUISITION_FORM_FIELDS.URINE_MEDICATION, categorySemantics.medication ? 'Yes' : 'Off');
        setMappedField(REQUISITION_FORM_FIELDS.URINE_OPIATES, categorySemantics.opiates ? 'Yes' : 'Off');
      } else if (sampleKind === 'hair') {
        // Explicitly set checkbox values for Haare
        setMappedField(REQUISITION_FORM_FIELDS.HAIR_ALCOHOL, categorySemantics.alcohol ? 'Yes' : 'Off');
        setMappedField(REQUISITION_FORM_FIELDS.HAIR_DRUGS, categorySemantics.drugs ? 'Yes' : 'Off');
        setMappedField(REQUISITION_FORM_FIELDS.HAIR_MEDICATION, categorySemantics.medication ? 'Yes' : 'Off');
        setMappedField(REQUISITION_FORM_FIELDS.HAIR_OPIATES, categorySemantics.opiates ? 'Yes' : 'Off');

        // Extract hair sample data from both sample_data and direct properties
        if (sample) {
          // Check for hair color in both places
          if (sample.hair_color || (sample.sample_data && sample.sample_data.hair_color)) {
            const hairColor = (sample.hair_color || (sample.sample_data && sample.sample_data.hair_color)).toString();
            appendMappedField(REQUISITION_FORM_FIELDS.HAIR_COLOR, hairColor);
            console.log('Setting hair color:', hairColor);
          }
          
          // Check for hair length in both places
          if (sample.hair_length || (sample.sample_data && sample.sample_data.hair_length)) {
            const hairLength = (sample.hair_length || (sample.sample_data && sample.sample_data.hair_length)).toString();
            appendMappedField(REQUISITION_FORM_FIELDS.HAIR_LENGTH, hairLength);
            console.log('Setting hair length:', hairLength);
          }
          
          // Check for remaining hair length in both places
          if (sample.remaining_hair_length || (sample.sample_data && sample.sample_data.remaining_hair_length)) {
            const remainingLength = (sample.remaining_hair_length || (sample.sample_data && sample.sample_data.remaining_hair_length)).toString();
            appendMappedField(REQUISITION_FORM_FIELDS.HAIR_REMAINING_LENGTH, remainingLength);
            console.log('Setting remaining hair length:', remainingLength);
          }
          
          // Check for extraction site in both places
          if (sample.extraction_site || (sample.sample_data && sample.sample_data.extraction_site)) {
            const extractionSite = sample.extraction_site || (sample.sample_data && sample.sample_data.extraction_site);
            const isHead = extractionSite === 'Kopf' ? 'Yes' : 'Off';
            const isOther = extractionSite === 'Andere' ? 'Yes' : 'Off';
            appendMappedField(REQUISITION_FORM_FIELDS.EXTRACTION_SITE_HEAD, isHead);
            appendMappedField(REQUISITION_FORM_FIELDS.EXTRACTION_SITE_OTHER, isOther);
            console.log('Setting extraction site:', extractionSite, 'isHead:', isHead, 'isOther:', isOther);
            
            // Check for extraction site details
            const extractionSiteDetails = 
                (extractionSite === 'Andere' && sample.extraction_site_details) || 
                (extractionSite === 'Andere' && sample.sample_data && sample.sample_data.extraction_site_andere);
                
            if (extractionSiteDetails) {
              const siteDetails = extractionSiteDetails.toString();
              appendMappedField(REQUISITION_FORM_FIELDS.EXTRACTION_SITE_DETAILS, siteDetails);
              console.log('Setting extraction site details:', siteDetails);
            }
          }
          
          // Check for cosmetic treatment in both places
          if (sample.cosmetic_treatment || (sample.sample_data && sample.sample_data.cosmetic_treatment)) {
            const cosmeticTreatment = sample.cosmetic_treatment || (sample.sample_data && sample.sample_data.cosmetic_treatment);
            const isNone = cosmeticTreatment === 'Keine' ? 'Yes' : 'Off';
            const isTreated = cosmeticTreatment === 'Behandelt' ? 'Yes' : 'Off';
            appendMappedField(REQUISITION_FORM_FIELDS.COSMETIC_TREATMENT_NONE, isNone);
            appendMappedField(REQUISITION_FORM_FIELDS.COSMETIC_TREATMENT_YES, isTreated);
            console.log('Setting cosmetic treatment:', cosmeticTreatment, 'isNone:', isNone, 'isTreated:', isTreated);
            
            // Check for cosmetic treatment details
            const treatmentDetails = 
                (cosmeticTreatment === 'Behandelt' && sample.cosmetic_treatment_details) || 
                (cosmeticTreatment === 'Behandelt' && sample.sample_data && sample.sample_data.cosmetic_treatment_behandelt);
                
            if (treatmentDetails) {
              const details = treatmentDetails.toString();
              appendMappedField(REQUISITION_FORM_FIELDS.COSMETIC_TREATMENT_DETAILS, details);
              console.log('Setting cosmetic treatment details:', treatmentDetails);
            }
          }
        }
      }
      else if (sampleKind === 'blood') {
        setMappedField(REQUISITION_FORM_FIELDS.BLOOD_ALCOHOL, categorySemantics.alcohol ? 'Yes' : 'Off');
      }

      // "Immer aktiv" überschreibt die automatische Erkennung.
      checkboxFields.forEach((standardFieldName) => {
        if (checkboxActivationConfig[standardFieldName]) {
          setMappedField(standardFieldName, 'Yes');
        }
      });

      // Nicht zugeordnete, direkt erkannte Felder können ebenfalls erzwungen aktiviert werden.
      Object.entries(directFieldActivationConfig).forEach(([pdfFieldName, shouldActivate]) => {
        if (shouldActivate) {
          formData.set(pdfFieldName, 'Yes');
        }
      });

      // Log the form data for debugging
      console.log('Form data being sent:');
      for (const [key, value] of formData.entries()) {
        console.log(`${key}: ${typeof value === 'object' ? 'File object' : `"${value}"`}`);
      }

      const pdfFillingServiceUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/fill-pdf-form`;
      
      console.log('Sending data to PDF filling service...');
      
      const response = await fetch(pdfFillingServiceUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`
        },
        body: formData,
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(
          errorData?.details || 
          `PDF filling service returned ${response.status} ${response.statusText}`
        );
      }
      
      const filledPdf = await response.blob();
      
      const url = URL.createObjectURL(filledPdf);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Anforderungsschein_${customer.last_name}_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(a);
      a.click();
      
      setSuccess('Anforderungsschein wurde erfolgreich erstellt und ausgefüllt.');
      
      URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
    } catch (err: any) {
      console.error('Error generating requisition form:', err);
      setError('Fehler beim Erstellen des Anforderungsscheins: ' + (err.message || 'Unbekannter Fehler'));
    } finally {
      setGenerating(false);
    }
  };

  if (!show) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-xl w-full max-w-md">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
            <FileText className="h-5 w-5 mr-2 text-primary-600" />
            Anforderungsschein erstellen
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
          </div>
        ) : (
          <>
            {error && (
              <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/50 text-red-700 dark:text-red-300 rounded-md text-sm flex items-start gap-2">
                <AlertCircle className="h-5 w-5 flex-shrink-0 mt-0.5" />
                <span>{error}</span>
              </div>
            )}
            
            {success && (
              <div className="mb-4 p-3 bg-green-50 dark:bg-green-900/50 text-green-700 dark:text-green-300 rounded-md text-sm flex items-start gap-2">
                <Check className="h-5 w-5 flex-shrink-0 mt-0.5" />
                <span>{success}</span>
              </div>
            )}
            
            <div className="space-y-4">
              {templates.length === 0 ? (
                <div className="bg-yellow-50 dark:bg-yellow-900/20 p-4 rounded-lg">
                  <div className="flex items-start gap-3">
                    <Info className="h-5 w-5 text-yellow-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm text-yellow-700 dark:text-yellow-300">
                        Keine Anforderungsschein-Vorlagen gefunden. Bitte laden Sie zuerst eine Vorlage hoch.
                      </p>
                      <Link
                        to="/forms"
                        className="mt-2 inline-flex items-center text-sm text-primary-600 hover:text-primary-700 dark:text-primary-400"
                      >
                        <FileText className="h-4 w-4 mr-1" />
                        Zu den Anforderungsscheinen
                      </Link>
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg">
                    <div className="flex items-start gap-2">
                      <Info className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <p className="text-sm text-blue-700 dark:text-blue-300">
                          Erstellen Sie einen Anforderungsschein für die Probe vom {sample.collection_date 
                            ? new Date(sample.collection_date).toLocaleDateString('de-DE') 
                            : new Date(sample.scheduled_date).toLocaleDateString('de-DE')}.
                        </p>
                        <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                          Der Anforderungsschein wird automatisch mit den Daten des Kunden und der Probe ausgefüllt.
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Anforderungsschein-Vorlage
                    </label>
                    <select
                      value={selectedTemplateId}
                      onChange={(e) => setSelectedTemplateId(e.target.value)}
                      className="w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700"
                    >
                      {templates.map(template => (
                        <option key={template.id} value={template.id}>
                          {template.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  <div className="bg-gray-50 dark:bg-gray-900/50 p-3 rounded-lg">
                    <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Daten für den Anforderungsschein</h4>
                    <div className="grid grid-cols-1 gap-2 text-sm sm:grid-cols-2">
                      <div>
                        <p className="text-gray-500 dark:text-gray-400">Kunde:</p>
                        <p className="font-medium text-gray-900 dark:text-white">
                          {customer?.first_name} {customer?.last_name}
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-500 dark:text-gray-400">Probenart:</p>
                        <p className="font-medium text-gray-900 dark:text-white">
                          {sample.sample_type_name}
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-500 dark:text-gray-400">Datum:</p>
                        <p className="font-medium text-gray-900 dark:text-white">
                          {sample.collection_date 
                            ? new Date(sample.collection_date).toLocaleDateString('de-DE')
                            : new Date(sample.scheduled_date).toLocaleDateString('de-DE')}
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-500 dark:text-gray-400">Zeit:</p>
                        <p className="font-medium text-gray-900 dark:text-white">
                          {sample.collection_time && typeof sample.collection_time === 'string' 
                            ? sample.collection_time.substring(0, 5) 
                            : sample.scheduled_time && typeof sample.scheduled_time === 'string'
                            ? sample.scheduled_time.substring(0, 5) 
                            : '-'}
                        </p>
                      </div>
                    </div>
                    
                    {substances.length > 0 && (
                      <div className="mt-3">
                        <p className="text-gray-500 dark:text-gray-400 mb-1">Analysen:</p>
                        <div className="flex flex-wrap gap-1">
                          {(() => {
                            const categories = new Set(substances.map(s => s.category_name));
                            return Array.from(categories).map((category, idx) => (
                              <span
                                key={idx}
                                className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200"
                              >
                                {category}
                              </span>
                            ));
                          })()}
                        </div>
                      </div>
                    )}
                    
                    {sample.sample_data && Object.keys(sample.sample_data).length > 0 && (
                      <div className="mt-3">
                        <p className="text-gray-500 dark:text-gray-400 mb-1">Probendetails:</p>
                        <div className="text-xs text-gray-700 dark:text-gray-300">
                          {sample.sample_type_name === 'Haar' && (
                            <>
                              {sample.sample_data.hair_length && <div>Haarlänge: {sample.sample_data.hair_length} cm</div>}
                              {sample.sample_data.hair_color && <div>Haarfarbe: {sample.sample_data.hair_color}</div>}
                              {sample.sample_data.extraction_site && <div>Entnahmestelle: {sample.sample_data.extraction_site}</div>}
                              {sample.sample_data.cosmetic_treatment && <div>Behandlung: {sample.sample_data.cosmetic_treatment}</div>}
                              {sample.sample_data.remaining_hair_length && <div>Resthaarlänge: {sample.sample_data.remaining_hair_length}</div>}
                            </>
                          )}
                          {sample.sample_type_name === 'Urin' && (
                            <>
                              {sample.sample_data.temperature && <div>Temperatur: {sample.sample_data.temperature} °C</div>}
                              {sample.sample_data.ph && <div>pH-Wert: {sample.sample_data.ph}</div>}
                            </>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </>
              )}
              
              <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-md"
                >
                  Abbrechen
                </button>
                <button
                  type="button"
                  onClick={generateRequisitionForm}
                  disabled={generating || templates.length === 0}
                  className="px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-md disabled:opacity-50 flex items-center"
                  title="Erstellt einen ausgefüllten Anforderungsschein basierend auf der ausgewählten Vorlage"
                >
                  {generating ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      Wird erstellt...
                    </>
                  ) : (
                    <>
                      <Download className="h-4 w-4 mr-2" />
                      Anforderungsschein erstellen
                    </>
                  )}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default RequisitionFormModal;
